import React, { useMemo } from "react";
import { Requirement, RequirementCategory } from "../../types";
import { Code, Award, DollarSign, Users, ShieldCheck, HelpCircle } from "lucide-react";

interface CategoryRollupProps {
  requirements: Requirement[];
  activeCategory: string;
  onSelectCategory: (category: string) => void;
}

interface CategoryConfig {
  key: RequirementCategory;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const CATEGORY_CONFIGS: CategoryConfig[] = [
  { key: "technical", label: "Technical", icon: Code },
  { key: "qualifications", label: "Qualifications", icon: Award },
  { key: "pricing", label: "Pricing", icon: DollarSign },
  { key: "management", label: "Management", icon: Users },
  { key: "compliance", label: "Compliance", icon: ShieldCheck },
  { key: "general", label: "General", icon: HelpCircle },
];

export const CategoryRollup: React.FC<CategoryRollupProps> = ({
  requirements,
  activeCategory,
  onSelectCategory,
}) => {
  const stats = useMemo(() => {
    const map = new Map<RequirementCategory, { total: number; confirmed: number }>();
    CATEGORY_CONFIGS.forEach((c) => map.set(c.key, { total: 0, confirmed: 0 }));

    requirements.forEach((req) => {
      const entry = map.get(req.category) || { total: 0, confirmed: 0 };
      entry.total += 1;
      if (req.confirmed) entry.confirmed += 1;
      map.set(req.category, entry);
    });

    return map;
  }, [requirements]);

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {CATEGORY_CONFIGS.map(({ key, label, icon: Icon }) => {
        const data = stats.get(key) || { total: 0, confirmed: 0 };
        const percentage = data.total > 0 ? Math.round((data.confirmed / data.total) * 100) : 0;
        const isActive = activeCategory === key;

        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelectCategory(isActive ? "all" : key)}
            className={`btn-animate flex flex-col justify-between rounded-xl border p-3 text-left transition-all shadow-xs ${
              isActive
                ? "border-[#2ba5b5] bg-[#2ba5b5]/10 ring-2 ring-[#2ba5b5]/30 dark:bg-[#2ba5b5]/15"
                : "border-slate-200/80 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
            }`}
          >
            <div className="flex items-center justify-between gap-1.5">
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-900 dark:text-slate-100">
                <Icon className={`h-3.5 w-3.5 ${isActive ? "text-[#2ba5b5]" : "text-slate-400"}`} />
                {label}
              </span>
              <span
                className={`text-[10px] font-bold ${
                  isActive
                    ? "text-[#1b7582] dark:text-[#52c8d7]"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              >
                {data.total}
              </span>
            </div>

            <div className="mt-2.5 space-y-1">
              <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
                <span>{data.confirmed} confirmed</span>
                <span className="font-semibold">{percentage}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    percentage === 100
                      ? "bg-emerald-500"
                      : percentage > 0
                        ? "bg-[#2ba5b5]"
                        : "bg-transparent"
                  }`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
